import dayjs from 'dayjs';
import { FilterIcon, LoaderCircleIcon, PlusIcon } from 'lucide-react';

import { ScrapingJobStatus, type GoogleSearchJob } from '../../types/types';
import { ScrapingJobStatusBadge } from '../ScrapingJobStatusBadge';
import { showMemoModal } from '../showMemoModal';
import { DeactivateScrapingJobButton } from './DeactivateScrapingJobButton';
import { PostDownloadButton } from './PostDownloadButton';

import { getPlatformName, SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { SearchInput } from '@/shared/components/search-input';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';

interface Props {
  platform: SOURCE_PLATFORM_TYPE | undefined;
  onPlatformChange: (platform: SOURCE_PLATFORM_TYPE | undefined) => void;
  scrapingJobs: GoogleSearchJob[];
  total?: number;
  isFetching: boolean;
  onSearch: (query: string) => void;
  onOwnerSearch: (owner: string) => void;
  onJobTypeFilterChange: (value: 'all' | 'onetime' | 'periodic') => void;
  onStatusFilterChange: (value: 'all' | 'IN_PROGRESS' | 'DONE') => void;
  jobTypeFilter: 'all' | 'onetime' | 'periodic';
  statusFilter: 'all' | 'IN_PROGRESS' | 'DONE';
  onCreateNew: () => void;
}

const COLUMN_WIDTHS = [
  { name: 'action', width: '100px' },
  { name: 'platform', width: '100px' },
  { name: 'jobType', width: '100px' },
  { name: 'keyword', width: '137px' },
  { name: 'owner', width: '100px' },
  { name: 'status', width: '130px' },
  { name: 'postsScraped', width: '130px' },
  { name: 'dateRange', width: '240px' },
  { name: 'requestedDate', width: '160px' },
  { name: 'download', width: '96px' },
  { name: 'memo', width: '1fr' },
];

const GRID_TEMPLATE_COLUMNS = COLUMN_WIDTHS.map((col) => col.width).join(' ');

export function GoogleSearchScrapingTable({
  platform,
  onPlatformChange,
  scrapingJobs,
  total = 0,
  isFetching,
  onSearch,
  onOwnerSearch,
  onJobTypeFilterChange,
  onStatusFilterChange,
  jobTypeFilter,
  statusFilter,
  onCreateNew,
}: Props) {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-xl font-semibold">Scraping list ({total})</p>
          <p className="text-muted-foreground text-sm">
            View and manage your scraping requests. Track the progress of each request and stay
            updated.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            value={platform || 'all'}
            onValueChange={(value) => {
              if (value === 'all') {
                onPlatformChange(undefined);
              } else {
                onPlatformChange(value as SOURCE_PLATFORM_TYPE);
              }
            }}
          >
            <SelectTrigger className="w-fit min-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={'all'}>All</SelectItem>
              <SelectItem value={SOURCE_PLATFORM_TYPE.TIKTOK}>TikTok</SelectItem>
              <SelectItem value={SOURCE_PLATFORM_TYPE.INSTAGRAM}>Instagram</SelectItem>
              {/* <SelectItem value={SOURCE_PLATFORM_TYPE.YOUTUBE}>YouTube</SelectItem> */}
            </SelectContent>
          </Select>
          <SearchInput placeholder="Search by keyword" onSubmit={onSearch} />
          <SearchInput placeholder="Search by owner" onSubmit={onOwnerSearch} />
          <Button onClick={onCreateNew} variant="default">
            <PlusIcon />
            Create new
          </Button>
        </div>
      </div>
      <div className="relative p-6">
        {isFetching && (
          <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
            <LoaderCircleIcon className="h-8 w-8 animate-spin" />
          </div>
        )}
        <Table className={isFetching ? 'opacity-50' : ''}>
          <TableHeader>
            <TableRow gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
              <TableHead>Action</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Job Type
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                        <FilterIcon className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => onJobTypeFilterChange('all')}>
                        All {jobTypeFilter === 'all' && '✓'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onJobTypeFilterChange('onetime')}>
                        One-time {jobTypeFilter === 'onetime' && '✓'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onJobTypeFilterChange('periodic')}>
                        Periodic {jobTypeFilter === 'periodic' && '✓'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableHead>
              <TableHead>Keyword</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Status
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                        <FilterIcon className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => onStatusFilterChange('all')}>
                        All {statusFilter === 'all' && '✓'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onStatusFilterChange('IN_PROGRESS')}>
                        In Progress {statusFilter === 'IN_PROGRESS' && '✓'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onStatusFilterChange('DONE')}>
                        Done {statusFilter === 'DONE' && '✓'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableHead>
              <TableHead>Posts scraped</TableHead>
              <TableHead>Date range</TableHead>
              <TableHead className="text-right">Requested date</TableHead>
              <TableHead>Download</TableHead>
              <TableHead>Memo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scrapingJobs.map((job) => (
              <TableRow key={job._id} gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
                <TableCell>
                  {job.status === ScrapingJobStatus.InProgress && (
                    <DeactivateScrapingJobButton scrapingJobId={job._id} />
                  )}
                </TableCell>
                <TableCell>{getPlatformName(job.platform)}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {job.scrapingType === 'periodic' ? 'Periodic' : 'Onetime'}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-50 truncate font-medium">{job.query}</TableCell>
                <TableCell className="max-w-40 truncate">{job.ownerName.split('@')[0]}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <ScrapingJobStatusBadge status={job.status} />
                  </div>
                </TableCell>
                <TableCell>{job.scrapedItemCount.toLocaleString()}</TableCell>
                <TableCell>
                  {job.scrapingType === 'periodic' ? (
                    <>
                      {dayjs(job.documentCreatedAt).add(1, 'day').format(DATE_FORMATS.base)} →
                      ongoing
                    </>
                  ) : job.startDate === '' || job.endDate === '' ? (
                    <span className="text-muted-foreground">In progress</span>
                  ) : (
                    <>
                      {dayjs(job.startDate).format(DATE_FORMATS.base)} →{' '}
                      {dayjs(job.endDate != null ? job.endDate : new Date()).format(
                        DATE_FORMATS.base,
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {dayjs(job.documentCreatedAt).format(DATE_FORMATS.base)}
                </TableCell>
                <TableCell>
                  <PostDownloadButton scrapingJobId={job._id} />
                </TableCell>
                <TableCell>
                  {job.memo ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-left"
                      onClick={async () => {
                        await showMemoModal({
                          memo: job.memo || '',
                          title: `Memo`,
                        });
                      }}
                    >
                      <span className="max-w-[120px] truncate">{job.memo}</span>
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
